import { pages } from '../data/pages'
import { policyCopy } from '../data/policies'
import { fill } from './copy'
import { daysSince, isRetinoid, isVitaminC, lastVisit, weeksSince } from './account'
import type { Product, Treatment } from '../core/types/catalog'
import type { Account, PlanItem } from '../core/types/account'
import type { PortalTip } from '../core/types/pages'

export type SkinTip = { title: string; body: string; productId?: string }

type Lookup = { productById: (id: string) => Product | undefined; treatmentBySlug: (slug: string) => Treatment | undefined }

/** Vult een beheerbare tip in met de tokens van dit moment. */
const render = (tip: PortalTip, extra: Record<string, string | number> = {}, productId?: string): SkinTip => ({
  title: fill(tip.title, extra),
  body: fill(tip.body, extra),
  productId,
})

/**
 * Eén tip die nu het meest ertoe doet. Eén, niet vijf: het dashboard blijft rustig.
 * De site kiest wélke tip past; de woorden komen uit Paginateksten → Mijn omgeving.
 * Gebruikt door Mijn omgeving én het klantdossier in het beheer.
 */
export function currentTip(account: Account, items: PlanItem[], { productById, treatmentBySlug }: Lookup): SkinTip {
  const tips = pages.portal.tips
  const withProduct = items
    .map((i) => ({ p: productById(i.productId), w: weeksSince(i.startedOn) }))
    .filter((x): x is { p: Product; w: number } => Boolean(x.p))

  const retinal = withProduct.find((x) => isRetinoid(x.p) && x.w >= 1 && x.w <= 6)
  if (retinal) return render(tips.retinoidWeek, { week: retinal.w + 1, product: retinal.p.name }, retinal.p.id)

  const vitC = withProduct.find((x) => isVitaminC(x.p) && x.w >= 4 && x.w <= 8)
  if (vitC) return render(tips.firstCycle, { weeks: vitC.w, product: vitC.p.name }, vitC.p.id)

  const fresh = withProduct.filter((x) => x.w === 0)
  if (fresh.length > 1) return render(tips.firstWeekMany, {}, fresh[0].p.id)
  if (fresh.length === 1) return render(tips.firstWeekOne, { product: fresh[0].p.name }, fresh[0].p.id)

  if (withProduct.length) return render(tips.patience)

  // Alleen salon
  const visit = lastVisit(account)
  const t = visit ? treatmentBySlug(visit.treatmentSlug) : undefined
  if (visit && t && daysSince(visit.date) <= 3) return render(tips.afterTreatment, { treatment: t.name })
  if (!visit && !account.course) return render(tips.whereToStart)
  if (account.course && account.course.done < account.course.total)
    return render(tips.betweenSessions, { courseSpacing: policyCopy.courseSpacing })
  return render(tips.keepResult)
}

export type SeasonNote = { key: string; title: string; body: string }

/** Kleine seizoensmelding, afgestemd op haar producten. */
export function seasonNote(items: PlanItem[], productById: (id: string) => Product | undefined, now = new Date()): SeasonNote | null {
  const seasons = pages.portal.seasons
  const m = now.getMonth() + 1
  const products = items.map((i) => productById(i.productId)).filter(Boolean) as Product[]
  const cream = products.find((p) => p.category === 'Crème')
  const retinal = products.find(isRetinoid)
  const spf = products.find((p) => p.category === 'SPF')
  const year = now.getFullYear()

  if (m >= 9 || m <= 2) {
    return {
      key: `koud-${m <= 2 ? year - 1 : year}`,
      title: fill(seasons.cold.title),
      body: cream ? fill(seasons.cold.withProduct, { product: cream.name }) : fill(seasons.cold.body),
    }
  }
  if (m >= 3 && m <= 5) {
    return {
      key: `lente-${year}`,
      title: fill(seasons.spring.title),
      body: spf ? fill(seasons.spring.withProduct, { product: spf.name }) : fill(seasons.spring.body),
    }
  }
  if (retinal) {
    return {
      key: `zomer-${year}`,
      title: fill(seasons.summerRetinal.title, { product: retinal.name }),
      body: fill(seasons.summerRetinal.body, { product: retinal.name }),
    }
  }
  return null
}
